import {
  CareerSiteBrandConfigurationV2Response,
  CareerSiteFiltersOption,
} from 'api-clients/monolith';
import { getFilterOptionDisplayText } from 'constants/v2/job-options';
import { getSortOptions } from 'constants/v2/sort-options';
import {
  ActiveFiltersV2,
  getDefaultActiveFilters,
  Segment,
} from 'contexts/v2/app-context';
import queryString from 'query-string';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { unstable_batchedUpdates as batchUpdates } from 'react-dom';
import { IntlShape, useIntl } from 'react-intl';

const URL_FILTER_KEYS = new Set([
  'category',
  'position',
  'locationName',
  'country',
  'state',
  'city',
  'job_type',
  'job_hours',
  'sort_by',
  'radius',
  'query',
  'page',
  'location',
  'place_id',
]);

const isMultiSelectFilter = (key: keyof ActiveFiltersV2): boolean => {
  return [
    'category',
    'position',
    'locationName',
    'country',
    'state',
    'city',
    'jobHours',
    'jobType',
  ].includes(key);
};

const createFilterDataFromParams = (
  params: queryString.ParsedQuery,
  brandConfig: CareerSiteBrandConfigurationV2Response,
  intl: IntlShape,
): ActiveFiltersV2 => {
  const filterData: ActiveFiltersV2 = {
    ...getDefaultActiveFilters(),
    category: [],
    position: [],
    locationName: [],
    country: [],
    state: [],
    city: [],
    jobHours: [],
    jobType: [],
  };

  const filterOptionSources: Record<
    keyof ActiveFiltersV2,
    CareerSiteFiltersOption[] | undefined
  > = {
    category: brandConfig?.filter_options?.category,
    position: brandConfig?.filter_options?.position,
    locationName: brandConfig?.filter_options?.locationName,
    country: brandConfig?.filter_options?.countries,
    state: brandConfig?.filter_options?.states,
    city: brandConfig?.filter_options?.cities,
    jobHours: brandConfig?.filter_options?.job_hours,
    jobType: brandConfig?.filter_options?.job_types,
  };

  (Object.keys(filterOptionSources) as (keyof ActiveFiltersV2)[]).forEach(
    filterKey => {
      const options = filterOptionSources[filterKey];
      const paramValue = params[filterKey];

      if (paramValue && options) {
        if (isMultiSelectFilter(filterKey)) {
          const values = Array.isArray(paramValue) ? paramValue : [paramValue];

          const matches = values
            .map(value => {
              let match = options.find(opt => opt.key === value);

              if (!match && options?.length) {
                match = options.find(
                  opt => opt.key.toLowerCase() === value.toLowerCase(),
                );
              }

              if (match) {
                // For jobType and jobHours, internationalize the label
                if (
                  (filterKey === 'jobType' || filterKey === 'jobHours') &&
                  intl
                ) {
                  return {
                    ...match,
                    label: getFilterOptionDisplayText(
                      match.key,
                      filterKey,
                      intl,
                    ),
                  };
                }
                return match;
              }

              return null;
            })
            .filter(Boolean) as CareerSiteFiltersOption[];

          if (matches.length > 0) {
            filterData[filterKey] = matches;
          }
        } else {
          const match = options.find(opt => opt.key === paramValue);
          if (match) {
            filterData[filterKey] = [match];
          }
        }
      }
    },
  );

  return filterData;
};

interface useUrlFiltersProps {
  brandConfig: CareerSiteBrandConfigurationV2Response | undefined;
  search: string;
  activeFiltersV2: ActiveFiltersV2;
  setActiveFiltersV2: Dispatch<SetStateAction<ActiveFiltersV2>>;
  setPage: (page: number) => void;
  setPlaceId: Dispatch<SetStateAction<string | null>>;
  setIsJobsFromCurrentLocation: Dispatch<SetStateAction<boolean>>;
  setJobSearchQuery: Dispatch<SetStateAction<string | null>>;
  setLocation: Dispatch<SetStateAction<Segment>>;
  setDisplayLocation?: Dispatch<SetStateAction<string | null>>;
  setRadius: Dispatch<SetStateAction<CareerSiteFiltersOption | null>>;
  setSortBy: Dispatch<SetStateAction<CareerSiteFiltersOption>>;
  // Current state values for comparison
  location: Segment;
  radius: CareerSiteFiltersOption | null;
  sortBy: CareerSiteFiltersOption;
  jobSearchQuery: string | null;
}

export const useUrlFiltersV2 = ({
  brandConfig,
  search,
  setActiveFiltersV2,
  setPage,
  setPlaceId,
  setIsJobsFromCurrentLocation,
  setJobSearchQuery,
  setLocation,
  setDisplayLocation,
  setRadius,
  setSortBy,
}: useUrlFiltersProps) => {
  const intl = useIntl();
  const brandConfigRef = useRef(brandConfig);

  useEffect(() => {
    brandConfigRef.current = brandConfig;
  }, [brandConfig]);

  const applyUrlParamsToState = useCallback(
    (searchString: string) => {
      const config = brandConfigRef.current;
      if (!config) return;

      const params = queryString.parse(searchString, {
        arrayFormat: 'bracket',
      });

      batchUpdates(() => {
        const filterData = createFilterDataFromParams(params, config, intl);
        setActiveFiltersV2(filterData);

        if (params.page) {
          const pageNumber = parseInt(params.page as string, 10);
          if (!Number.isNaN(pageNumber) && pageNumber > 0) {
            setPage(pageNumber);
          }
        } else {
          setPage(1);
        }

        const placeId = params.place_id as string;
        setPlaceId(placeId || null);

        const query = params.query as string;
        setJobSearchQuery(query || null);

        const locationParam = params.location as string;
        if (locationParam) {
          setLocation({ id: null, value: locationParam });
          if (setDisplayLocation) setDisplayLocation(locationParam);
          setIsJobsFromCurrentLocation(false);
        } else {
          setLocation({ id: null, value: 'current_location' });
          setIsJobsFromCurrentLocation(true);
        }

        const radiusParam = params.radius as string;
        if (radiusParam && config?.radius_options) {
          const matchingRadius = config.radius_options.find(
            option => option.key === radiusParam,
          );
          if (matchingRadius) {
            setRadius(matchingRadius);
          }
        }

        const sortByParam = params.sort_by as string;
        if (sortByParam) {
          const sortOptions = getSortOptions(intl);
          const matchingSort = sortOptions.find(
            option => option.key === sortByParam,
          );
          if (matchingSort) {
            setSortBy(matchingSort);
          }
        }
      });
    },
    [
      intl,
      setActiveFiltersV2,
      setPage,
      setPlaceId,
      setJobSearchQuery,
      setLocation,
      setDisplayLocation,
      setIsJobsFromCurrentLocation,
      setRadius,
      setSortBy,
    ],
  );

  const isInitialRenderRef = useRef(true);

  useEffect(() => {
    if (!brandConfig || !isInitialRenderRef.current) {
      return;
    }

    isInitialRenderRef.current = false;

    const params = queryString.parse(search, { arrayFormat: 'bracket' });
    const hasAnyFilterParam = Object.keys(params).some(key =>
      URL_FILTER_KEYS.has(key),
    );

    if (hasAnyFilterParam) {
      applyUrlParamsToState(search);
    }
  }, [brandConfig, search, applyUrlParamsToState]);

  useEffect(() => {
    const handlePopState = () => {
      applyUrlParamsToState(window.location.search);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [applyUrlParamsToState]);
};
